/**
 * Typed API module for project environments (staging copies of a project).
 * Mirrors backend/models/project_environment_models.py and
 * backend/routers/environment_router.py.
 *
 * Create and deploy are separate operations. POST /environments only reserves
 * the name, URL and ports — nothing is built and the URL will not resolve.
 * POST /{id}/deploy is what copies the project's code in and starts it.
 *
 * Create is synchronous and answers 201. Deploy answers 202 and finishes in
 * the background — poll GET /{id} (or the list) until the status leaves
 * `deploying`.
 */

import api from '../utils/axios';

export type EnvironmentStatus =
  /** Name, URL and ports reserved — nothing built, nothing running.
   *  Create is synchronous, so this is the state an environment is *born* in;
   *  there is no transient create state and no failed-create state. */
  | 'created'
  /** Copying code, building, starting. */
  | 'deploying'
  | 'deploy_failed'
  /** Built and observed running at least once. */
  | 'deployed';

/** Where an environment takes its code from.
 *
 *  A `main` is fed by the project — that is what Deploy has always meant. A
 *  `sub` belongs to a main's pipeline and can only ever run what that main
 *  runs; it has no deploy of its own, only a promote. */
export type EnvironmentType = 'main' | 'sub';

/** How far an environment is from the code it would next take — the project's
 *  HEAD for a main, its main's deployed commit for a sub.
 *
 *  Measured over *content* lineage, not raw git ancestry: a rollback restores
 *  an old tree as a **new** commit, so ancestry keeps answering "linear, N
 *  behind" long after the code has moved backwards past an environment. */
export type PipelineState =
  | 'never_deployed'
  | 'in_sync'
  | 'behind'
  /** A rollback moved the source back past this environment: it runs code the
   *  source has abandoned, and deploying would discard it. */
  | 'ahead'
  /** Both at once — each side holds work the other does not. */
  | 'diverged'
  | 'unknown';

export interface ProjectEnvironment {
  id: string;
  project_id: string;
  environment_name: string;
  status: EnvironmentStatus;
  /** Set on a sub; null on a main. Fixed at creation. */
  parent_environment_id?: string | null;
  type: EnvironmentType;
  /** The subdomain this environment is served on. Always stored — the user's
   *  choice, or the default filled in at creation. */
  custom_domain?: string | null;
  /** Never changes after creation except on rename or a URL change. */
  url?: string | null;
  deployed_commit_hash?: string | null;
  /** Stamped by the list endpoint only — absent on a single create or GET. */
  pipeline_state?: PipelineState | null;
  /** Commits a deploy or promote would apply. */
  changes_behind?: number | null;
  /** Commits this environment runs that its source has abandoned — a deploy
   *  would discard them. */
  changes_ahead?: number | null;
  /** The commit whose content this environment actually runs. Differs from
   *  `deployed_commit_hash` when that was a rollback commit, which carries its
   *  target's tree and is never shown as itself. */
  commit_hash?: string | null;
  /** What that commit was called in the chat. Null for a commit with no
   *  checkpoint — a `/git/push`, the initial commit, an external pull. */
  checkpoint_title?: string | null;
  /** Where this environment and its source split. Diverged only. */
  fork_commit_hash?: string | null;
  fork_checkpoint_title?: string | null;
  /** Epoch seconds, matching the rest of the platform. */
  deployed_at?: number | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: number | null;
  updated_at?: number | null;
  /** Populated when status is 'deploy_failed'. */
  error?: string | null;
  /** Raw build output — the tail of the Docker log, up to 20 lines.
   * Shown behind the 'View error details' toggle in the details view. */
  error_detail?: string | null;
  /** Stable value to branch on; never switch on the text of `error`. */
  error_code?: string | null;
}

/** Where the project's own code stands right now.
 *
 *  Sent once for the whole list rather than per environment: every main is
 *  measured against this same thing. `commit_hash` is the *effective* commit —
 *  if the project sits on a rollback, this is the commit that rollback
 *  restored, never the rollback itself. */
export interface AppPosition {
  commit_hash?: string | null;
  checkpoint_title?: string | null;
}

export interface EnvironmentListResponse {
  environments: ProjectEnvironment[];
  count: number;
  /** Null when the project has no repository. */
  current_app?: AppPosition | null;
}

export interface EnvironmentAvailabilityResult {
  available: boolean;
  /** Present when not available — why. */
  reason?: string | null;
}

/** What has moved since this environment last decided or deployed. */
export type EnvironmentCredentialState = 'unchanged' | 'new' | 'changed' | 'removed';

/** Where an environment takes a credential's value from.
 *
 *  `project` stores **no value** — it resolves from the project at deploy time,
 *  which is why such a credential can never be stale, and equally why an older
 *  value cannot be kept once the project's has moved. */
export type EnvironmentCredentialSource = 'project' | 'manual';

export interface EnvironmentCredential {
  key_name: string;
  display_name: string;
  description: string;
  value_type?: string | null;
  source: EnvironmentCredentialSource;
  state: EnvironmentCredentialState;
  has_value: boolean;
  /** The value this environment will actually deploy with — its own when it
   *  overrides, the project's otherwise. Present only for `plain_text`
   *  credentials; a `secure` value is never returned, the same rule the
   *  project credentials list already applies. */
  value?: string | null;
  /** What reverting this row goes back to. Sent only when the environment
   *  overrides a `plain_text` credential — that is the one case where `value`
   *  above is not already the project's. */
  project_value?: string | null;
}

export interface EnvironmentCredentialsSummary {
  items: EnvironmentCredential[];
  /** Something is new, changed or removed — worth stopping the user for. */
  has_changes: boolean;
}

export interface EnvironmentCredentialDecision {
  key_name: string;
  source: EnvironmentCredentialSource;
  /** Only read for `manual`. Omit to keep the value already stored. */
  value?: string;
}

export interface EnvironmentDeployCheckResult {
  direction: 'forward' | 'backward';
  data_loss: boolean;
  /** Why a deploy would lose data — branch on this, not on `message`. */
  reason:
    | 'none'
    | 'migration_removed'
    | 'database_unreachable'
    | 'history_unreadable'
    /** The environment has a database and the incoming code has none. */
    | 'database_removed';
  current_commit_hash?: string;
  incoming_commit_hash?: string;
  pending_migrations: string[];
  /** Migrations this environment applied that the incoming code no longer has. */
  removed_migrations: string[];
  message: string;
  /** The check can't read this project's migrations, so it didn't run. The
   *  deploy keeps the database, and `message` says the check was skipped —
   *  show it before deploying. */
  migration_check_skipped?: boolean;
  /** null when the project has no credentials at all. */
  credentials?: EnvironmentCredentialsSummary | null;
}

export interface EnvironmentDeployResponse {
  id: string;
  status: EnvironmentStatus;
  message: string;
}

export interface EnvironmentDeleteResponse {
  /** Cleanup is best-effort and the record always goes, so this is about the
   * deletion — read `warnings` to know whether anything was left behind. */
  warnings?: string[];
  success: boolean;
  message: string;
}

/** Live container state — separate from `status`, which describes provisioning.
 * An environment can be `deployed` and not running (e.g. after a host reboot). */
export type EnvironmentRuntimeStatus =
  | 'running'
  | 'stopped'
  | 'starting'
  | 'stopping'
  | 'partial'
  | 'error'
  | 'unknown';

export interface EnvironmentServiceState {
  name: string;
  service: string;
  state: string;
  status: string;
  /** '' when the container declares no healthcheck — fine, not unhealthy. */
  health: string;
}

export interface EnvironmentRuntime {
  status: EnvironmentRuntimeStatus;
  /** A start or stop is running for this environment. */
  in_progress: boolean;
  services: EnvironmentServiceState[];
}

export interface EnvironmentRuntimeStatusResponse {
  /** Keyed by environment id, to merge into the list you already hold. */
  environments: Record<string, EnvironmentRuntime>;
  count: number;
}

export interface EnvironmentOperationResponse {
  status: 'starting' | 'stopping';
  message: string;
}

const base = (projectId: string) => `/dev-agent/projects/${projectId}/environments`;

export const projectEnvironmentApi = {
  async listEnvironments(projectId: string): Promise<EnvironmentListResponse> {
    const response = await api.get(base(projectId));
    return response.data;
  },

  async getEnvironment(projectId: string, environmentId: string): Promise<ProjectEnvironment> {
    const response = await api.get(`${base(projectId)}/${environmentId}`);
    return response.data;
  },

  /** Is this name free within the project? Name only — the URL has its own
   *  check below. Pass `environmentId` when editing so the environment is not
   *  reported as colliding with itself. */
  async checkName(
    projectId: string,
    environmentName: string,
    environmentId?: string
  ): Promise<EnvironmentAvailabilityResult> {
    const response = await api.get(`${base(projectId)}/check-name`, {
      params: {
        environment_name: environmentName,
        ...(environmentId ? { environment_id: environmentId } : {}),
      },
    });
    return response.data;
  },

  /** Is this subdomain label free? Checked against project custom domains and
   *  environment URLs — they share one namespace. Pass `environmentId` when
   *  editing so the environment's own URL is not a collision. */
  async checkDomain(
    projectId: string,
    customDomain: string,
    environmentId?: string
  ): Promise<EnvironmentAvailabilityResult> {
    const response = await api.get(`${base(projectId)}/check-domain`, {
      params: {
        custom_domain: customDomain,
        ...(environmentId ? { environment_id: environmentId } : {}),
      },
    });
    return response.data;
  },

  /** 201 — reserves name, URL and ports, synchronously. Nothing is built here;
   * deploy() is what makes the environment real.
   *
   * There is nothing to poll for: it either resolves with a `created`
   * environment or rejects with the reason, having created nothing. */
  async createEnvironment(
    projectId: string,
    environmentName: string,
    customDomain?: string,
    parentEnvironmentId?: string
  ): Promise<ProjectEnvironment> {
    const response = await api.post(base(projectId), {
      environment_name: environmentName,
      // Omitted when empty — the backend fills in its default.
      ...(customDomain ? { custom_domain: customDomain } : {}),
      // Omitted for a main. Present, it makes this a sub of that main — which
      // does not need to be deployed yet; creating only reserves a name, a URL
      // and ports.
      ...(parentEnvironmentId ? { parent_environment_id: parentEnvironmentId } : {}),
    });
    return response.data;
  },

  /** Rename, and optionally move the environment to a different URL. Instant
   *  (no rebuild) — but a moved URL stops resolving at its old address
   *  straight away.
   *
   *  Renaming never changes the URL. A non-empty `customDomain` moves the
   *  environment to that URL; empty leaves it unchanged. */
  async updateEnvironment(
    projectId: string,
    environmentId: string,
    environmentName: string,
    customDomain: string = ''
  ): Promise<ProjectEnvironment> {
    const response = await api.patch(`${base(projectId)}/${environmentId}`, {
      environment_name: environmentName,
      custom_domain: customDomain,
    });
    return response.data;
  },

  /** Record what this environment does about each of the project's credentials.
   *
   *  Called after the user confirms and before `deploy()` — deploy-check is a
   *  read-only GET, so the decisions need their own write. They persist, so a
   *  deploy that then fails does not lose them. */
  async updateCredentials(
    projectId: string,
    environmentId: string,
    credentials: EnvironmentCredentialDecision[]
  ): Promise<EnvironmentCredentialsSummary> {
    const response = await api.put(`${base(projectId)}/${environmentId}/credentials`, {
      credentials,
    });
    return response.data;
  },

  /** Always call before showing the deploy confirmation dialog. */
  async deployCheck(
    projectId: string,
    environmentId: string
  ): Promise<EnvironmentDeployCheckResult> {
    const response = await api.get(`${base(projectId)}/${environmentId}/deploy-check`);
    return response.data;
  },

  /** Copies the project's current code in and starts it — the only operation
   * that builds. Also the repair for a `created` or
   * `deploy_failed` environment. Send confirmDataLoss=true only after the user
   * confirmed a backward deploy-check. */
  async deploy(
    projectId: string,
    environmentId: string,
    confirmDataLoss: boolean
  ): Promise<EnvironmentDeployResponse> {
    const response = await api.post(`${base(projectId)}/${environmentId}/deploy`, {
      confirm_data_loss: confirmDataLoss,
    });
    return response.data;
  },

  /** Removes containers, volume, images, folder, ports and the record. */
  async deleteEnvironment(
    projectId: string,
    environmentId: string
  ): Promise<EnvironmentDeleteResponse> {
    const response = await api.delete(`${base(projectId)}/${environmentId}`);
    return response.data;
  },

  /** Runtime state of every environment in one call — poll this, there is
   * deliberately no per-environment status route. Cached ~10s server-side. */
  async getRuntimeStatus(projectId: string): Promise<EnvironmentRuntimeStatusResponse> {
    const response = await api.get(`${base(projectId)}/status`);
    return response.data;
  },

  /** Brings a stopped environment's containers back up — seconds, no rebuild. */
  async startEnvironment(
    projectId: string,
    environmentId: string
  ): Promise<EnvironmentOperationResponse> {
    const response = await api.post(`${base(projectId)}/${environmentId}/start`);
    return response.data;
  },

  /** Stops the containers; the database volume is kept. */
  async stopEnvironment(
    projectId: string,
    environmentId: string
  ): Promise<EnvironmentOperationResponse> {
    const response = await api.post(`${base(projectId)}/${environmentId}/stop`);
    return response.data;
  },
};

export default projectEnvironmentApi;
